const SOLID_UNITS = [
  { value: "gram", label: "grams (g)" },
  { value: "oz", label: "ounces (oz)" },
  { value: "lb", label: "pounds (lb)" },
  { value: "kg", label: "kilograms (kg)" },
];

const LIQUID_UNITS = [
  { value: "fl oz", label: "fluid ounces (fl oz)" },
  { value: "pt", label: "pints (pt)" },
  { value: "qt", label: "quarts (qt)" },
  { value: "gal", label: "gallons (gal)" },
  { value: "ml", label: "milliliters (mL)" },
  { value: "l", label: "liters (L)" },
];

const OZ_PER_LB = 16;
const FL_OZ_PER_PT = 16;
const FL_OZ_PER_QT = 32;

const COMMON_FRACTIONS = {
  0.125: "\u215B",
  0.25: "\u00BC",
  0.375: "\u215C",
  0.5: "\u00BD",
  0.625: "\u215D",
  0.75: "\u00BE",
  0.875: "\u215E",
};

export function formatCommonFraction(value) {
  const whole = Math.floor(value);
  const fractional = value - whole;

  if (Math.abs(fractional) < 0.001) {
    return whole.toString();
  }

  for (const [key, char] of Object.entries(COMMON_FRACTIONS)) {
    if (Math.abs(fractional - Number(key)) < 0.001) {
      return whole > 0 ? `${whole}${char}` : char;
    }
  }

  return formatFractionDecimal(value);
}

function formatMetricDecimal(value) {
  const rounded = Math.round(value * 100) / 100;
  return rounded.toFixed(2).replace(/\.?0+$/, "");
}

function formatFractionDecimal(value) {
  if (value === Math.floor(value)) {
    return value.toString();
  }
  const rounded = Math.round(value * 100) / 100;
  if (rounded === Math.floor(rounded)) {
    return rounded.toString();
  }
  return rounded.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

function formatMetricWeight(grams) {
  if (grams >= 1000) {
    return `${formatMetricDecimal(grams / 1000)}kg`;
  }
  return `${Math.round(grams)}g`;
}

export function formatSolidStatement(
  grams,
  weightUnits,
  { randomPackage = false, showMetric = true, useCommonFractions = false } = {}
) {
  const ozPerGram = 1 / weightUnits["oz"];
  const lbPerGram = 1 / weightUnits["lb"];
  const fmt = useCommonFractions ? formatCommonFraction : formatFractionDecimal;

  const totalOz = grams * ozPerGram;
  const totalLb = grams * lbPerGram;
  const metric = formatMetricWeight(grams);

  const parts = [];
  let hasParenthetical = false;

  if (randomPackage) {
    parts.push(`NET WT ${fmt(totalLb)} LB`);
  } else if (totalLb < 1) {
    parts.push(`NET WT ${fmt(totalOz)} OZ`);
  } else if (totalLb < 4) {
    let parenthetical;
    if (useCommonFractions) {
      parenthetical = `${fmt(totalLb)} LB`;
    } else {
      const wholeLb = Math.floor(totalLb);
      const remainingOz = Math.round((totalLb - wholeLb) * OZ_PER_LB);

      if (remainingOz === 0 || remainingOz === 16) {
        const adjustedLb = remainingOz === 16 ? wholeLb + 1 : wholeLb;
        parenthetical = `${adjustedLb} LB`;
      } else {
        parenthetical = `${wholeLb} LB ${remainingOz} OZ`;
      }
    }

    parts.push(`NET WT ${fmt(totalOz)} OZ (${parenthetical})`);
    hasParenthetical = true;
  } else {
    parts.push(`NET WT ${fmt(totalLb)} LB`);
  }

  if (showMetric) {
    parts.push(hasParenthetical ? metric : `(${metric})`);
  }

  return parts.join(" ");
}

export function formatLiquidStatement(
  ml,
  volumeUnits,
  { showMetric = true, useCommonFractions = false } = {}
) {
  const flOzPerMl = 1 / volumeUnits["fl oz"];
  const ptPerMl = 1 / volumeUnits["pt"];
  const galPerMl = 1 / volumeUnits["gal"];
  const fmt = useCommonFractions ? formatCommonFraction : formatFractionDecimal;

  const totalFlOz = ml * flOzPerMl;
  const totalPt = ml * ptPerMl;
  const totalGal = ml * galPerMl;

  const parts = [];
  let hasParenthetical = false;

  if (totalPt < 1) {
    parts.push(`${fmt(totalFlOz)} FL OZ`);
  } else if (totalGal < 1) {
    let parenthetical;
    if (useCommonFractions) {
      const wholeQt = Math.floor(totalFlOz / FL_OZ_PER_QT);
      const remainingPt = (totalFlOz - wholeQt * FL_OZ_PER_QT) / FL_OZ_PER_PT;
      const subParts = [];
      if (wholeQt > 0) subParts.push(`${wholeQt} QT`);
      if (remainingPt > 0.001) subParts.push(`${fmt(remainingPt)} PT`);
      parenthetical = subParts.join(" ") || `${fmt(totalPt)} PT`;
    } else {
      const wholeQt = Math.floor(totalFlOz / FL_OZ_PER_QT);
      const remainingAfterQt = totalFlOz - wholeQt * FL_OZ_PER_QT;
      const wholePt = Math.floor(remainingAfterQt / FL_OZ_PER_PT);
      const remainingOz = Math.round(remainingAfterQt - wholePt * FL_OZ_PER_PT);

      const subParts = [];
      if (wholeQt > 0) subParts.push(`${wholeQt} QT`);
      if (wholePt > 0) subParts.push(`${wholePt} PT`);
      if (remainingOz > 0) subParts.push(`${remainingOz} FL OZ`);
      parenthetical =
        subParts.length > 0 ? subParts.join(" ") : wholeQt > 0 ? `${wholeQt} QT` : `${wholePt} PT`;
    }

    parts.push(`${fmt(totalFlOz)} FL OZ (${parenthetical})`);
    hasParenthetical = true;
  } else {
    parts.push(`${fmt(totalGal)} GAL`);
  }

  if (showMetric) {
    const metric = ml >= 1000 ? `${formatMetricDecimal(ml / 1000)} L` : `${Math.round(ml)} mL`;
    parts.push(hasParenthetical ? metric : `(${metric})`);
  }

  return parts.join(" ");
}

export function initFdaNetQuantityStatement() {
  const calculator = document.querySelector("#nqsCalculator");
  if (!calculator) return;

  const weightUnits = JSON.parse(calculator.dataset.weightUnits);
  const volumeUnits = JSON.parse(calculator.dataset.volumeUnits);

  const form = document.querySelector("#nqsForm");
  const unitSelect = document.querySelector("#unitSelect");
  const quantityInput = form.querySelector("[name='quantity']");
  const productTypeSelect = form.querySelector("[name='product_type']");
  const showMetricCheckbox = document.querySelector("#showMetric");
  const commonFractionsCheckbox = document.querySelector("#commonFractions");
  const randomPackageCheckbox = document.querySelector("#randomPackage");
  const missingMessage = document.querySelector("#nqsMissingValues");
  const resultContainer = document.querySelector("#nqsResultContainer");
  const resultDisplay = document.querySelector("#nqsResult");
  const copyButton = document.querySelector("#nqsCopyButton");
  const shareButton = document.querySelector("#nqsShareButton");

  function updateUnitOptions(selectedUnit = null) {
    const productType = productTypeSelect.value;
    const units = productType === "solid" ? SOLID_UNITS : LIQUID_UNITS;

    unitSelect.innerHTML = "";
    units.forEach((unit) => {
      const option = document.createElement("option");
      option.value = unit.value;
      option.textContent = unit.label;
      if (selectedUnit && unit.value === selectedUnit) {
        option.selected = true;
      }
      unitSelect.appendChild(option);
    });

    randomPackageCheckbox.closest("label").classList.toggle("is-hidden", productType !== "solid");
    if (productType !== "solid") {
      randomPackageCheckbox.checked = false;
    }

    updateStatement();
  }

  function updateStatement() {
    const formData = new FormData(form);
    const quantity = parseFloat(formData.get("quantity"));
    const unit = formData.get("unit");
    const productType = formData.get("product_type");
    const randomPackage = randomPackageCheckbox.checked;
    const showMetric = showMetricCheckbox.checked;

    if (!quantity || quantity <= 0 || !unit) {
      missingMessage.classList.remove("is-hidden");
      resultContainer.classList.add("is-hidden");
      return;
    }

    missingMessage.classList.add("is-hidden");
    resultContainer.classList.remove("is-hidden");

    const useCommonFractions = commonFractionsCheckbox.checked;

    let result;
    if (productType === "solid") {
      const conversionFactor = weightUnits[unit];
      const grams = quantity * conversionFactor;
      result = formatSolidStatement(grams, weightUnits, {
        randomPackage,
        showMetric,
        useCommonFractions,
      });
    } else {
      const conversionFactor = volumeUnits[unit];
      const ml = quantity * conversionFactor;
      result = formatLiquidStatement(ml, volumeUnits, { showMetric, useCommonFractions });
    }

    resultDisplay.innerHTML = `
      <div class="nqs-statement">${result}</div>
    `;
  }

  function copyToClipboard() {
    const fullStatement = resultDisplay.querySelector(".nqs-statement")?.textContent || "";

    navigator.clipboard.writeText(fullStatement).then(() => {
      const originalText = copyButton.textContent;
      copyButton.textContent = "Copied!";
      copyButton.classList.add("is-success");

      setTimeout(() => {
        copyButton.textContent = originalText;
        copyButton.classList.remove("is-success");
      }, 2000);
    });
  }

  function getShareUrl() {
    const params = new URLSearchParams();
    params.set("product_type", productTypeSelect.value);
    params.set("quantity", quantityInput.value);
    params.set("unit", unitSelect.value);
    if (!showMetricCheckbox.checked) {
      params.set("show_metric", "false");
    }
    if (commonFractionsCheckbox.checked) {
      params.set("common_fractions", "true");
    }
    if (randomPackageCheckbox.checked) {
      params.set("random_package", "true");
    }
    return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
  }

  function copyShareLink() {
    const url = getShareUrl();
    navigator.clipboard.writeText(url).then(() => {
      const originalText = shareButton.textContent;
      shareButton.textContent = "Copied!";
      shareButton.classList.add("is-success");
      shareButton.classList.remove("is-outlined");

      setTimeout(() => {
        shareButton.textContent = originalText;
        shareButton.classList.remove("is-success");
        shareButton.classList.add("is-outlined");
      }, 2000);
    });
  }

  function loadFromQueryParams() {
    const params = new URLSearchParams(window.location.search);

    const productType = params.get("product_type");
    if (productType && ["solid", "liquid"].includes(productType)) {
      productTypeSelect.value = productType;
    }

    const unit = params.get("unit");
    updateUnitOptions(unit);

    const quantity = params.get("quantity");
    if (quantity) {
      quantityInput.value = quantity;
    }

    const showMetric = params.get("show_metric");
    if (showMetric === "false") {
      showMetricCheckbox.checked = false;
    }

    const commonFractions = params.get("common_fractions");
    if (commonFractions === "true") {
      commonFractionsCheckbox.checked = true;
    }

    const randomPackage = params.get("random_package");
    if (randomPackage === "true") {
      randomPackageCheckbox.checked = true;
    }

    updateStatement();
  }

  productTypeSelect.addEventListener("change", () => updateUnitOptions());
  form.addEventListener("input", updateStatement);
  form.addEventListener("change", updateStatement);
  copyButton.addEventListener("click", copyToClipboard);
  shareButton.addEventListener("click", copyShareLink);

  loadFromQueryParams();
}
